import {
  formatCurrency,
  formatMaybeCurrency,
  formatPercent,
} from '@shared/SharedAccountBody';
import { PositionRow } from './PortfolioPositionsTable';

export interface RealizedSummary {
  amount: number;
  percent?: number;
  soldQty: number;
}

interface PortfolioHoldingsCardProps {
  position: PositionRow;
  realized?: RealizedSummary;
}

const signedCurrency = (v: number) => `${v >= 0 ? '+' : ''}${formatCurrency(v)}`;

function formatDate(iso?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const PortfolioHoldingsCard = ({ position, realized }: PortfolioHoldingsCardProps) => {
  const plUp = position.unrealizedPL !== undefined && position.unrealizedPL >= 0;
  const boughtOn = formatDate(position.heldSince);
  const soldOn = formatDate(position.lastSoldAt);
  const hasExit = realized !== undefined && realized.soldQty > 0;
  const exitUp = hasExit && realized!.amount >= 0;


  const statValueTone = (up?: boolean) =>
    up === undefined ? 'text-foreground/90' : up ? 'text-positive' : 'text-negative';

  return (
    <section className="mt-5 rounded-xl border border-border bg-[color:rgba(255,255,255,0.02)] p-4">
      <h3 className="m-0 mb-3.5 font-display text-[0.95rem] font-semibold text-foreground">
        Your position
      </h3>

      {hasExit && (
        <div className="mb-[18px] grid grid-cols-1 gap-3 min-[560px]:grid-cols-2">
          <div
            className={`flex flex-col gap-[3px] rounded-xl border p-4 ${
              exitUp
                ? 'border-positive/25 bg-positive/8'
                : 'border-negative/25 bg-negative/8'
            }`}
          >
            <span className="text-[11px] uppercase tracking-[0.04em] text-muted-foreground">
              Total Realized Profit
            </span>
            <span
              className={`font-display text-[1.45rem] font-bold ${
                exitUp ? 'text-positive' : 'text-negative'
              }`}
            >
              {signedCurrency(realized!.amount)}
            </span>
            {realized!.percent !== undefined && (
              <span className="text-xs text-muted-foreground">
                {formatPercent(realized!.percent)}
              </span>
            )}
          </div>
          {position.unrealizedPL !== undefined && (
            <div className="flex flex-col gap-[3px] rounded-xl border border-secondary/28 bg-secondary/8 p-4">
              <span className="text-[11px] uppercase tracking-[0.04em] text-muted-foreground">
                Unrealized profit
              </span>
              <span className="font-display text-[1.45rem] font-bold text-secondary">
                {signedCurrency(position.unrealizedPL)}
              </span>
              {position.unrealizedPLPct !== undefined && (
                <span className="text-xs font-semibold text-secondary">
                  {formatPercent(position.unrealizedPLPct)}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-x-4 gap-y-3.5 min-[768px]:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-[11px] uppercase tracking-[0.04em] text-muted-foreground">
            Quantity
          </span>
          <span className="text-sm font-semibold text-foreground/90">
            {position.qty !== undefined ? position.qty : '—'}
          </span>
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-[11px] uppercase tracking-[0.04em] text-muted-foreground">
            Avg entry
          </span>
          <span className="text-sm font-semibold text-foreground/90">
            {formatMaybeCurrency(position.avgEntryPrice)}
          </span>
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-[11px] uppercase tracking-[0.04em] text-muted-foreground">
            Current
          </span>
          <span className="text-sm font-semibold text-foreground/90">
            {formatMaybeCurrency(position.currentPrice)}
          </span>
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-[11px] uppercase tracking-[0.04em] text-muted-foreground">
            Market value
          </span>
          <span className="text-sm font-semibold text-foreground/90">
            {formatMaybeCurrency(position.marketValue)}
          </span>
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-[11px] uppercase tracking-[0.04em] text-muted-foreground">
            Unrealized P/L
          </span>
          <span
            className={`text-sm font-semibold ${statValueTone(
              position.unrealizedPL !== undefined ? plUp : undefined,
            )}`}
          >
            {position.unrealizedPL === undefined
              ? '—'
              : `${plUp ? '+' : ''}${formatCurrency(position.unrealizedPL)}`}
            {position.unrealizedPLPct !== undefined &&
              ` (${formatPercent(position.unrealizedPLPct)})`}
          </span>
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-[11px] uppercase tracking-[0.04em] text-muted-foreground">
            Weight
          </span>
          <span className="text-sm font-semibold text-foreground/90">
            {position.weightPct.toFixed(2)}%
          </span>
        </div>
        {hasExit && (
          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-[11px] uppercase tracking-[0.04em] text-muted-foreground">
              Realized (exits)
            </span>
            <span className={`text-sm font-semibold ${statValueTone(exitUp)}`}>
              {`${exitUp ? '+' : ''}${formatCurrency(realized!.amount)}`}
              {realized!.percent !== undefined &&
                ` (${formatPercent(realized!.percent)})`}
            </span>
          </div>
        )}
      </div>

      {(boughtOn || soldOn) && (
        <div className="mt-4 flex flex-wrap gap-2.5 border-t border-[color:rgba(255,255,255,0.06)] pt-3.5">
          {boughtOn && (
            <span className="inline-flex items-center gap-2 text-[13px] text-foreground/75">
              <span className="rounded-[5px] bg-positive px-[7px] py-0.5 text-[10px] font-bold tracking-[0.04em] text-[color:var(--nq-bg-deep)]">
                BUY
              </span>
              {boughtOn}
            </span>
          )}
          {soldOn && (
            <span className="inline-flex items-center gap-2 text-[13px] text-foreground/75">
              <span className="rounded-[5px] bg-negative px-[7px] py-0.5 text-[10px] font-bold tracking-[0.04em] text-foreground">
                SOLD
              </span>
              {soldOn}
            </span>
          )}
        </div>
      )}
    </section>
  );
};

export default PortfolioHoldingsCard;
